import React from 'react';

import { RadioOptionsWithConfirm, SelectWithConfirm } from 'shared-components';

import QuestionPage from '../../../templates/question';
import { Q } from '../../../../store/student/types';
import { InputFieldConfig } from '../../../../types';
import { partnerVisaStatusLabel } from './utils';
import { PartnerVisaStatus } from 'types';
import { useDispatch } from 'react-redux';
import { useCustomNavigate } from '../../../../hooks/useCustomNavigate';
import { saveHistory } from '../../../../store/student/actions';
import { createUpdateOfferFieldAction } from '../../../../store/student/offer/actions';
import { Pages } from '../../../../store/student/history/types';

export interface ICustomProps {
  sectionTitle: string;
  complete: boolean;
  currentStep: number;
  steps: number;
  hasDependents?: boolean;
  familyMembersTravellingVisible: boolean;
  familyMembersTravellingConfig: InputFieldConfig;
  hasPartner?: boolean;
  spouseTravellingVisible: boolean;
  spouseTravellingConfig: InputFieldConfig;
  partnerVisaStatus?: PartnerVisaStatus;
  partnerVisaStatusVisible: boolean;
  partnerVisaStatusConfig: InputFieldConfig;
  hasChildren?: boolean;
  childrenTravellingVisible: boolean;
  childrenTravellingConfig: InputFieldConfig;
  childrenNum: number;
  childrenTravellingNumberVisible: boolean;
  childrenTravellingNumberConfig: InputFieldConfig;
  config: any;
}

type IProps = ICustomProps;

const HistoryQPage3: React.FC<IProps> = (props: IProps) => {
  const dispatch = useDispatch();
  const onNext = (config: any) => dispatch(saveHistory(Pages.PAGE3, config));
  const onChange = (fieldName: string, value: any) => dispatch(createUpdateOfferFieldAction(fieldName, value));

  const { navigateToStudentDashboard } = useCustomNavigate();
  const onBackToTaskList = () => navigateToStudentDashboard();

  const handleYesNoClick = (target: Q, question: any) => {
    onChange(target, question.char === 'Y');
  };

  const handlePartnerVisaStatusClick = (question: any) => {
    onChange(
      Q.PARTNER_VISA_STATUS,
      question.char === '1'
        ? PartnerVisaStatus.APPLYING_DEPENDANT
        : question.char === '2'
          ? PartnerVisaStatus.APPLYING_DIFFERENT
          : question.char === '3'
            ? PartnerVisaStatus.READY_TO_TRAVEL_VALID
            : PartnerVisaStatus.ALREADY_IN_UK_VALID,
    );
  };

  return (
    <QuestionPage
      complete={props.complete}
      currentStep={props.currentStep}
      steps={props.steps}
      onNext={() => onNext(props.config)}
      onBackToTaskList={onBackToTaskList}
      section={props.sectionTitle}
    >
      {props.familyMembersTravellingVisible && (
        <RadioOptionsWithConfirm
          key={Q.FAMILY_MEMBERS_TRAVELLING}
          id={Q.FAMILY_MEMBERS_TRAVELLING}
          value={props.hasDependents}
          label={'Do you have anyone coming with you to the UK?'}
          inputHelperText={props.familyMembersTravellingConfig?.helpText}
          onClick={(question: any) => {
            handleYesNoClick(Q.FAMILY_MEMBERS_TRAVELLING, question);
          }}
          isInline={true}
          questions={[
            {
              char: 'Y',
              label: 'Yes',
              selected: props.hasDependents === true,
            },
            {
              char: 'N',
              label: 'No',
              selected: props.hasDependents === false,
            },
          ]}
        />
      )}

      {props.spouseTravellingVisible && (
        <RadioOptionsWithConfirm
          key={Q.SPOUSE_TRAVELLING}
          id={Q.SPOUSE_TRAVELLING}
          value={props.hasPartner}
          label={'Do you have a partner that is either applying for a UK visa or has a UK visa?'}
          inputHelperText={props.spouseTravellingConfig?.helpText}
          onClick={(question: any) => handleYesNoClick(Q.SPOUSE_TRAVELLING, question)}
          isInline={true}
          questions={[
            {
              char: 'Y',
              label: 'Yes',
              selected: props.hasPartner === true,
            },
            {
              char: 'N',
              label: 'No',
              selected: props.hasPartner === false,
            },
          ]}
        />
      )}

      {props.partnerVisaStatusVisible && (
        <RadioOptionsWithConfirm
          key={Q.PARTNER_VISA_STATUS}
          id={Q.PARTNER_VISA_STATUS}
          value={props.partnerVisaStatus}
          label={'My partner is...'}
          inputHelperText={props.partnerVisaStatusConfig?.helpText}
          onClick={(question: any) => handlePartnerVisaStatusClick(question)}
          questions={[
            {
              char: '1',
              label: partnerVisaStatusLabel(PartnerVisaStatus.APPLYING_DEPENDANT),
              selected: props.partnerVisaStatus === PartnerVisaStatus.APPLYING_DEPENDANT,
            },
            {
              char: '2',
              label: partnerVisaStatusLabel(PartnerVisaStatus.APPLYING_DIFFERENT),
              selected: props.partnerVisaStatus === PartnerVisaStatus.APPLYING_DIFFERENT,
            },
            {
              char: '3',
              label: partnerVisaStatusLabel(PartnerVisaStatus.READY_TO_TRAVEL_VALID),
              selected: props.partnerVisaStatus === PartnerVisaStatus.READY_TO_TRAVEL_VALID,
            },
            {
              char: '4',
              label: partnerVisaStatusLabel(PartnerVisaStatus.ALREADY_IN_UK_VALID),
              selected: props.partnerVisaStatus === PartnerVisaStatus.ALREADY_IN_UK_VALID,
            },
          ]}
        />
      )}

      {props.childrenTravellingVisible && (
        <RadioOptionsWithConfirm
          key={Q.CHILDREN_TRAVELLING}
          id={Q.CHILDREN_TRAVELLING}
          value={props.hasChildren}
          label={'Are you applying for your Student visa with dependant children?'}
          onClick={(question: any) => {
            handleYesNoClick(Q.CHILDREN_TRAVELLING, question);
            onChange(Q.CHILDREN_TRAVELLING_NUMBER, 0);
          }}
          isInline={true}
          questions={[
            {
              char: 'Y',
              label: 'Yes',
              selected: props.hasChildren === true,
            },
            {
              char: 'N',
              label: 'No',
              selected: props.hasChildren === false,
            },
          ]}
        />
      )}

      {props.childrenTravellingNumberVisible && (
        <SelectWithConfirm
          key={Q.CHILDREN_TRAVELLING_NUMBER}
          id={Q.CHILDREN_TRAVELLING_NUMBER}
          label="How many children?"
          options={[
            { label: '1', value: '1' },
            { label: '2', value: '2' },
            { label: '3', value: '3' },
            { label: '4', value: '4' },
            { label: '5', value: '5' },
            { label: '6', value: '6' },
            { label: '7', value: '7' },
            { label: '8', value: '8' },
            { label: '9', value: '9' },
          ]}
          value={props.childrenNum}
          onChange={(evt: any) => onChange(Q.CHILDREN_TRAVELLING_NUMBER, Number(evt.currentTarget.value))}
          inputHelperText={props.childrenTravellingNumberConfig?.helpText}
        />
      )}
    </QuestionPage>
  );
};

export default HistoryQPage3;
